import React, { useState } from 'react';
import './ChangePassword.css';
import { useChangePasswordMutation } from '../auth/api/authApi';
import Toast from '../../components/ToastNotification';
import Eye from '../../assets/icons/Eye';

const FIELDS = [
  { name: 'currentPassword', label: 'Current Password', placeholder: 'Enter current password' },
  { name: 'newPassword',     label: 'New Password',     placeholder: 'Enter new password (5–20 characters)' },
  { name: 'confirmPassword', label: 'Confirm New Password', placeholder: 'Re-enter new password' },
];

const ChangePasswordPage = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [loading, setLoading] = useState(false);
  const [changePasswordApi] = useChangePasswordMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear the error for this field as the user types
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const toggleShow = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validate = () => {
    const errors = {};

    if (!formData.currentPassword) {
      errors.currentPassword = 'Current Password is required';
    }

    if (!formData.newPassword) {
      errors.newPassword = 'New Password is required';
    } else if (formData.newPassword.length < 5) {
      errors.newPassword = 'New Password must be at least 5 characters';
    } else if (formData.newPassword.length > 20) {
      errors.newPassword = 'New Password must not exceed 20 characters';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Confirm Password is required';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Confirm Password does not match New Password';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const response = await changePasswordApi({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      }).unwrap();

      if (response.success) {
        Toast.success(response.message || 'Password changed successfully');
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setFieldErrors({});
      }
    } catch (err) {
      const errorMessage = err?.data?.message || err?.message || 'Failed to change password';
      // Map backend "Current password is incorrect" to the right field
      if (errorMessage.toLowerCase().includes('current password')) {
        setFieldErrors({ currentPassword: errorMessage });
      } else {
        Toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-page">
      <div className="change-password-container">
        <div className="layout-header">
          <h2>Change Password</h2>
        </div>

        <div className="row"><hr /></div>

        <form onSubmit={handleSubmit} className="change-password-form">
          {FIELDS.map(({ name, label, placeholder }) => (
            <div className="form-group" key={name}>
              <label htmlFor={name}>{label}</label>
              <div className="input-wrapper">
                <input
                  type={showPassword[name] ? 'text' : 'password'}
                  id={name}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  className={`form-input${fieldErrors[name] ? ' input-error' : ''}`}
                  placeholder={placeholder}
                  disabled={loading}
                />
                <button
                  type="button"
                  className={`eye-toggle${showPassword[name] ? ' eye-toggle--visible' : ''}`}
                  onClick={() => toggleShow(name)}
                  tabIndex={-1}
                  aria-label={showPassword[name] ? `Hide ${label}` : `Show ${label}`}
                >
                  <Eye size={18} color="#718096" />
                </button>
              </div>
              {fieldErrors[name] && (
                <span className="field-error">{fieldErrors[name]}</span>
              )}
            </div>
          ))}

          <button
            type="submit"
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
